import React, { useState, useRef } from 'react';

import { openInputPicker } from '../../../utils/formUtils/showPickerUtils';

interface DateTimeFieldProps {
  id: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  allowFutureDates?: boolean;
}

const sanitizeDateTimeValue = (
  rawDateTime: string | undefined | null,
): string => {
  if (!rawDateTime) {
    return '';
  }

  // Match YYYY-MM-DDTHH:mm format (datetime-local)
  const match = rawDateTime.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!match) {
    return '';
  }

  const [, year, month, day, hours, minutes] = match;
  const yearNum = parseInt(year, 10);
  if (yearNum < 1900) {
    return '';
  }

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const DateTimeField: React.FC<DateTimeFieldProps> = ({
  id,
  value,
  onChange,
  disabled,
  allowFutureDates = true,
}) => {
  const safeDateTime = sanitizeDateTimeValue(value);
  const [showFutureError, setShowFutureError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Get current datetime in YYYY-MM-DDTHH:mm format (local timezone)
  const now = new Date();
  const maxDateTime = allowFutureDates
    ? undefined
    : `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}T${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const selectedDateTime = e.target.value;

    // If future dates are not allowed, validate the datetime on blur
    if (
      !allowFutureDates &&
      selectedDateTime &&
      maxDateTime &&
      selectedDateTime > maxDateTime
    ) {
      setShowFutureError(true);
      e.target.value = '';
      onChange(e as unknown as React.ChangeEvent<HTMLInputElement>);
    } else {
      setShowFutureError(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShowFutureError(false);
    onChange(e);
  };

  return (
    <div className="w-full">
      <input
        type="datetime-local"
        id={id}
        ref={inputRef}
        value={safeDateTime}
        onChange={handleChange}
        onBlur={handleBlur}
        onClick={() => openInputPicker(inputRef, disabled)}
        max={maxDateTime}
        className={`h-10 w-full rounded border px-4 text-sm outline-none transition-all cursor-pointer
          ${
            showFutureError
              ? 'border-red-500 dark:border-red-500'
              : 'border-gray-300 dark:border-white/20'
          }
          text-ink dark:bg-white/5 dark:text-white/80
          focus:border-primary dark:focus:border-primary
          disabled:cursor-not-allowed`}
        disabled={disabled}
      />
      {showFutureError && (
        <p className="mt-1 text-xs text-red-500">
          Future dates/times are not allowed
        </p>
      )}
    </div>
  );
};

export default DateTimeField;
